import { useState } from 'react';
import { createZodFormResolver, lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { Button, Dialog } from '@optimus-web/core';
import { FeatureFlag } from '@mews-commander/enums';
import { RateGroupOptions } from '@mews-commander/data-access';
import { PolicyException, PolicyExceptionsFormStep } from '../../../../types';
import { PolicyExceptionsDatesFieldName, PaymentPolicyFieldName, FieldSectionName } from '../../types';
import {
    PolicyExceptionsFormDatesFields,
    PolicyExceptionsFormDetailsFields,
    PolicyExceptionsFormSummaryContent,
} from '../fields';
import { usePaymentPoliciesValidate, usePolicyExceptionUpdate } from '../../../../mutations';
import {
    datesPolicyExceptionSchema,
    PolicyExceptionFormValues,
    policyExceptionSchema,
    toPolicyExceptionUpdateParameters,
} from '../../../../schemas';
import { useCurrentRateGroupContext, useCurrentServiceIdContext } from '../../../../contexts';

export const useEditPolicyExceptionDialogForm = ({ policyException }: { policyException: PolicyException }) =>
    useSteppedDialogForm<PolicyExceptionsFormStep>({
        id: 'editPolicyExceptionForm',
        title: lt('PolicyExceptionFormEditTitle'),
        steps: ['dates', 'details', 'summary'],
        stepLabels: {
            dates: lt('DatePlural'),
            details: lt('DetailPlural'),
            summary: lt('Summary'),
        },
        renderForm: (props) => <EditPolicyExceptionForm {...props} policyException={policyException} />,
    });

const EditPolicyExceptionForm = ({
    policyException,
    ...props
}: SteppedDialogFormProps<PolicyExceptionsFormStep> & { policyException: PolicyException }) => {
    const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
    const { policyId, cancellationPolicies, settlementRules, timeUnitIntervals, rateIds } = policyException;

    const validateTimeUnitIntervals = usePaymentPoliciesValidate(policyId);
    const { mutateAsync } = usePolicyExceptionUpdate();

    const { id: rateGroupId, options } = useCurrentRateGroupContext();
    const serviceId = useCurrentServiceIdContext();
    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    const defaultValues = {
        [FieldSectionName.Dates]: {
            [PolicyExceptionsDatesFieldName.TimeUnitIntervals]: timeUnitIntervals,
        },
        [FieldSectionName.Rates]: {
            ...(rateIds
                ? { [PaymentPolicyFieldName.ExceptionType]: 'rate' as const, [PaymentPolicyFieldName.Rates]: rateIds }
                : { [PaymentPolicyFieldName.ExceptionType]: 'rateGroup' as const }),
        },
        [PaymentPolicyFieldName.CancellationFees]: cancellationPolicies.map(({ id }) => id),
        [PaymentPolicyFieldName.SettlementRules]: settlementRules.map(({ id }) => id),
        ...(requirePaymentDuringOCI
            ? {
                  [PaymentPolicyFieldName.RequirePaymentCardDuringOnlineCheckIn]: options.includes(
                      RateGroupOptions.RequirePaymentCardDuringOnlineCheckIn
                  ),
              }
            : {}),
    };

    const handleCloseValidationDialog = () => {
        setIsValidationDialogOpen(false);
    };

    const stepMap = createStepMap({ exceptionType: rateIds ? 'rate' : 'rateGroup', policyId });
    const { renderFormContent } = useSteppedDialogFormControls<PolicyExceptionsFormStep, PolicyExceptionFormValues>({
        ...props,
        defaultValues,
        stepResolvers: {
            dates: createZodFormResolver(datesPolicyExceptionSchema),
            details: createZodFormResolver(policyExceptionSchema),
            summary: createZodFormResolver(policyExceptionSchema),
        },
        stepHandlers: {
            dates: async (values, { setCurrentStep }) => {
                await validateTimeUnitIntervals(values.dates.timeUnitIntervals, {
                    onSuccess: ({ collidingRateIds }) => {
                        if (rateIds?.some((rateId) => collidingRateIds.includes(rateId))) {
                            setIsValidationDialogOpen(true);
                        } else {
                            setCurrentStep('details');
                        }
                    },
                });
            },
        },
        mode: 'onChange',
        onSubmit: async (formValues) => {
            const params = policyExceptionSchema
                .transform(
                    toPolicyExceptionUpdateParameters({
                        rateGroupId,
                        serviceId,
                        policyOverrideId: policyException.policyId,
                    })
                )
                .parse({
                    ...formValues,
                    ...{
                        //  This is momentary while the feature is enable for all users. This will allow us to activate users ad they will have by default the option selected.
                        requirePaymentCardDuringOnlineCheckIn: requirePaymentDuringOCI
                            ? formValues.requirePaymentCardDuringOnlineCheckIn
                            : true,
                    },
                });
            await mutateAsync(params, {
                onSuccess: () => {
                    showSuccessAlert({ title: lt('PolicyExceptionEditSuccessMessage') });
                },
            });
        },
    });

    return renderFormContent((step) => (
        <>
            <Dialog
                active={isValidationDialogOpen}
                onClose={handleCloseValidationDialog}
                title={lt('PolicyExceptionCollidingRatesDialogTitle')}
                footer={{
                    primary: (
                        <Button onClick={handleCloseValidationDialog} variant="primary" type="submit">
                            {lt('Close')}
                        </Button>
                    ),
                }}
            >
                {lt('PolicyExceptionCollidingRateGroupDialogDescription')}
            </Dialog>
            {stepMap[step]}
        </>
    ));
};

const createStepMap = ({ policyId, exceptionType }: { policyId?: string; exceptionType: 'rate' | 'rateGroup' }) => ({
    dates: <PolicyExceptionsFormDatesFields />,
    details: (
        <PolicyExceptionsFormDetailsFields
            policyId={policyId}
            isExceptionSectionDisabled={exceptionType === 'rateGroup'}
        />
    ),
    summary: <PolicyExceptionsFormSummaryContent />,
});
